import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import CategoryModal from '../components/CategoryModal';
import CategoryTransactionsModal from '../components/CategoryTransactionsModal';
import TransactionModal from '../components/TransactionModal';

function Main({ currentMonth }) {
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [spending, setSpending] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [currentMonth]);

  async function loadData() {
    try {
      setLoading(true);
      const [categoriesData, budgetsData, spendingData] = await Promise.all([
        api.getCategories(),
        api.getBudgets(currentMonth),
        api.getSpending(currentMonth),
      ]);

      setCategories(categoriesData);
      
      const budgetMap = {};
      budgetsData.forEach(b => {
        budgetMap[b.category_id] = b.amount;
      });
      setBudgets(budgetMap);
      setSpending(spendingData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleBudgetUpdate(categoryId, amount) {
    try {
      await api.updateBudget({
        category_id: categoryId,
        month: currentMonth,
        amount: parseFloat(amount) || 0,
      });
      setBudgets(prev => ({ ...prev, [categoryId]: parseFloat(amount) || 0 }));
    } catch (error) {
      alert('Failed to update budget: ' + error.message);
    }
  }

  async function handleSaveCategory(categoryData) {
    try {
      if (editingCategory) {
        await api.updateCategory(editingCategory.id, categoryData);
      } else {
        await api.createCategory(categoryData);
      }
      setShowModal(false);
      setEditingCategory(null);
      loadData();
    } catch (error) {
      alert('Failed to save category: ' + error.message);
    }
  }

  async function handleDeleteCategory(id) {
    if (!window.confirm('Are you sure you want to delete this category? All associated transactions will also be deleted.')) {
      return;
    }
    
    try {
      await api.deleteCategory(id);
      loadData();
    } catch (error) {
      alert('Failed to delete category: ' + error.message);
    }
  }

  async function handleRegenerateBudget() {
    if (!window.confirm('Regenerate budget from previous month?\n\nThis will copy all budget amounts from the previous month to the current month. This only works if no transactions exist for the current month.')) {
      return;
    }

    try {
      await api.regenerateBudget(currentMonth);
      alert('Budget regenerated successfully from previous month!');
      loadData();
    } catch (error) {
      alert('Failed to regenerate budget:\n\n' + error.message);
    }
  }

  function handleCategoryNameClick(category) {
    setSelectedCategory(category);
    setShowTransactionsModal(true);
  }

  function handleEditTransaction(transaction) {
    setEditingTransaction(transaction);
    setShowTransactionModal(true);
  }

  async function handleSaveTransaction(transactionData) {
    try {
      if (editingTransaction) {
        await api.updateTransaction(editingTransaction.id, transactionData);
      } else {
        await api.createTransaction(transactionData);
      }
      setShowTransactionModal(false);
      setEditingTransaction(null);
      loadData(); // Reload to update spending
    } catch (error) {
      alert('Failed to save transaction: ' + error.message);
    }
  }

  function calculateTotal(type) {
    return categories
      .filter(c => c.type === type)
      .reduce((sum, c) => sum + (budgets[c.id] || 0), 0);
  }

  function renderCategory(category) {
    const budget = budgets[category.id] || 0;
    const spent = spending[category.id]?.spent || 0;
    const splitSent = spending[category.id]?.split_sent || 0;
    const splitReceived = spending[category.id]?.split_received || 0;
    // Actual spending = direct spending - splits sent to other categories + splits received from other categories
    const netSpent = spent - splitSent + splitReceived;
    const remaining = budget - netSpent;
    const percentage = budget > 0 ? (netSpent / budget) * 100 : 0;

    return (
      <div key={category.id} className="category-item">
        <div className="category-header">
          <div className="category-name-budget">
            <span 
              className="category-name clickable" 
              onClick={() => handleCategoryNameClick(category)}
              title="Click to view transactions"
            >
              {category.name}
            </span>
            {category.type === 'expense' && (
              <span className="category-spent">
                ${netSpent.toFixed(2)} / ${budget.toFixed(2)}
              </span>
            )}
            {category.type === 'income' && (
              <span className="category-budget-only">
                ${budget.toFixed(2)}
              </span>
            )}
          </div>
          <div className="category-actions">
            <button
              onClick={() => {
                setEditingCategory(category);
                setShowModal(true);
              }}
              className="btn-icon"
              title="Edit"
            >
              ✎
            </button>
            <button
              onClick={() => handleDeleteCategory(category.id)}
              className="btn-icon"
              title="Delete"
            >
              ✕
            </button>
          </div>
        </div>
        
        {category.type === 'expense' && budget > 0 && (
          <div className="progress-bar">
            <div
              className={`progress-fill ${percentage > 100 ? 'over-budget' : ''}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        )}

        <div className="category-budget-input">
          <label>Budget:</label>
          <input
            type="number"
            step="0.01"
            value={budget}
            onChange={(e) => handleBudgetUpdate(category.id, e.target.value)}
            className="input-budget"
          />
        </div>

        {category.type === 'expense' && budget > 0 && (
          <div className={`category-remaining ${remaining < 0 ? 'negative' : ''}`}>
            Remaining: ${remaining.toFixed(2)}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');
  const totalBudget = calculateTotal('expense');
  const totalIncome = calculateTotal('income');

  return (
    <div className="main-page">
      <div className="page-actions">
        <button onClick={() => { setEditingCategory(null); setShowModal(true); }} className="btn btn-primary">
          + Add Category
        </button>
        <button onClick={handleRegenerateBudget} className="btn btn-secondary">
          Regenerate from Previous Month
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card income-card">
          <div className="summary-label">Total Income</div>
          <div className="summary-value">${totalIncome.toFixed(2)}</div>
        </div>
        <div className="summary-card expense-card">
          <div className="summary-label">Total Budget</div>
          <div className="summary-value">${totalBudget.toFixed(2)}</div>
        </div>
        <div className="summary-card balance-card">
          <div className="summary-label">Net</div>
          <div className="summary-value">${(totalIncome - totalBudget).toFixed(2)}</div>
        </div>
      </div>

      <section className="category-section">
        <h2>Income Categories</h2>
        <div className="categories-list">
          {incomeCategories.length === 0 && (
            <p className="empty-state">No income categories yet. Add one to get started!</p>
          )}
          {incomeCategories.map(renderCategory)}
        </div>
      </section>

      <section className="category-section">
        <h2>Expense Categories</h2>
        <div className="categories-list">
          {expenseCategories.length === 0 && (
            <p className="empty-state">No expense categories yet. Add one to get started!</p>
          )}
          {expenseCategories.map(renderCategory)}
        </div>
      </section>

      {showModal && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onClose={() => {
            setShowModal(false);
            setEditingCategory(null);
          }}
        />
      )}

      {showTransactionsModal && selectedCategory && (
        <CategoryTransactionsModal
          category={selectedCategory}
          month={currentMonth}
          onClose={() => {
            setShowTransactionsModal(false);
            setSelectedCategory(null);
          }}
          onEditTransaction={handleEditTransaction}
        />
      )}

      {showTransactionModal && (
        <TransactionModal
          transaction={editingTransaction}
          categories={categories}
          onSave={handleSaveTransaction}
          onClose={() => {
            setShowTransactionModal(false);
            setEditingTransaction(null);
          }}
        />
      )}
    </div>
  );
}

export default Main;
